import { useState } from "react";
import { toast } from "sonner";
import { Gem, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const PRODUCTS = [
  {
    slug: "phoenix-pin",
    name: "The Phoenix Pin",
    tag: "Collector · Solid Cast",
    price: "$24",
    image: "/brand/phoenix-pin-solid.jpeg",
    blurb: "Die-struck gold with black hard enamel. Bird and Alaskan range fused into one sturdy piece — nothing thin to bend.",
  },
  {
    slug: "real-razors",
    name: "REAL RAZORS",
    tag: "Straight Razor · Etched Steel",
    price: "$189",
    image: "/brand/real-razors-hero.jpeg",
    blurb: "Mirror-steel blade etched REAL RAZORS, black-and-gold handle with an engraved phoenix. Built for the shave wall.",
  },
  {
    slug: "real-razors-set",
    name: "REAL RAZORS — Gift Set",
    tag: "Boxed · Razor + Strop",
    price: "$249",
    image: "/brand/real-razors-boxset.jpeg",
    blurb: "Rigid presentation box with gold-foil crest, straight razor and leather strop. Premium unboxing, ready to gift.",
  },
];

export default function SignatureCollection() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const openReserve = (p) => {
    setActive(p);
    setDone(false);
    setEmail("");
    setName("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email."); return; }
    try {
      setBusy(true);
      await api.post("/waitlist", { email, name, role: active.slug, source: "shop" });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-4" data-testid="signature-collection">
      <div className="flex items-center gap-2 text-[#D4AF37] mb-3">
        <Gem size={16} />
        <span className="font-mono text-xs uppercase tracking-widest">The Signature Collection</span>
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-bold max-w-2xl">
        Barbistry gear, forged for the wall.
      </h2>
      <p className="mt-3 text-neutral-400 max-w-xl">
        The house line — a solid collector pin and the REAL RAZORS straight-razor collection.
        Launching in limited runs. Reserve yours to lock the founding price.
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PRODUCTS.map((p) => (
          <div
            key={p.slug}
            data-testid={`signature-${p.slug}`}
            className="group relative bg-[#111] border border-white/10 overflow-hidden flex flex-col hover:border-[#D4AF37]/50 transition-colors"
          >
            <div className="relative overflow-hidden">
              <img
                src={p.image}
                alt={p.name}
                className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute top-3 left-3 flex items-center gap-1 bg-black/70 backdrop-blur border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-mono uppercase tracking-wider px-2 py-1">
                <Sparkles size={11} /> Coming soon
              </span>
            </div>
            <div className="p-5 flex flex-col flex-1">
              <p className="text-[11px] font-mono uppercase tracking-wider text-neutral-500">{p.tag}</p>
              <h3 className="mt-1 font-display text-xl font-semibold">{p.name}</h3>
              <p className="mt-2 text-sm text-neutral-400 flex-1">{p.blurb}</p>
              <div className="mt-5 flex items-center justify-between">
                <div className="leading-none">
                  <span className="block text-[10px] font-mono uppercase tracking-wider text-neutral-500">Founding price</span>
                  <span className="font-display text-2xl font-bold gold-text">{p.price}</span>
                </div>
                <Button
                  onClick={() => openReserve(p)}
                  data-testid={`reserve-${p.slug}`}
                  className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold group/btn"
                >
                  Reserve
                  <ArrowRight size={16} className="ml-1.5 transition-transform group-hover/btn:translate-x-1" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-[#D4AF37]/30 text-white rounded-none" data-testid="reserve-dialog">
          {done ? (
            <div className="py-6 text-center" data-testid="reserve-success">
              <CheckCircle2 size={40} className="mx-auto text-[#D4AF37]" />
              <p className="mt-4 font-display text-xl font-semibold">You're on the list.</p>
              <p className="mt-2 text-sm text-neutral-400">
                We'll email you the moment <span className="text-[#D4AF37]">{active?.name}</span> is ready to ship — at your locked founding price.
              </p>
              <Button
                onClick={() => setOpen(false)}
                data-testid="reserve-close"
                className="mt-6 rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold"
              >
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Reserve {active?.name}</DialogTitle>
                <DialogDescription className="text-neutral-400">
                  No payment now. Lock the founding price of <span className="text-[#D4AF37]">{active?.price}</span> and we'll notify you at launch.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3 mt-2" data-testid="reserve-form">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  data-testid="reserve-name"
                  className="bg-[#111] border-white/15 rounded-none focus-visible:ring-[#D4AF37]"
                />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  data-testid="reserve-email"
                  className="bg-[#111] border-white/15 rounded-none focus-visible:ring-[#D4AF37]"
                />
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="reserve-submit"
                  className="w-full rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-11"
                >
                  {busy ? "Reserving…" : "Reserve My Spot"}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
